<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="showOverlay"
        :class="$style['overlay']"
      >
        <Transition
          name="slide"
          @after-leave="showOverlay = false"
        >
          <div
            v-if="showGuide && currentStep"
            :class="$style.onboarding"
          >
            <div
              :class="[
                $style.onboarding__imageContainer,
                { [$style['onboarding__imageContainer--welcome']]: isWelcomeStep },
                { [$style['onboarding__imageContainer--steps']]: !isWelcomeStep },
              ]"
            >
              <div
                v-if="image"
                :class="$style.onboarding__image"
              >
                <img
                  :src="image.url"
                  :style="image.style ?? {}"
                  :alt="$t(currentStep.title)"
                />
              </div>
            </div>
            <div :class="$style.onboarding__content">
              <Heading1 :class="$style.onboarding__title">{{ $t(currentStep.title) }}</Heading1>

              <div :class="[$style.onboarding__description, 'richtext']">
                <Paragraph1
                  v-for="(p, i) of stepDescription"
                  :key="i"
                >
                  <span v-html="p" />
                </Paragraph1>
                <Paragraph1
                  v-if="readTime && isWelcomeStep"
                  :class="$style.readTime"
                >
                  {{ $t('onboarding.readTime', { minutes: readTime }) }}
                </Paragraph1>
              </div>
            </div>
            <div :class="$style.onboarding__actions">
              <template v-if="isWelcomeStep">
                <ButtonSecondary
                  width="auto"
                  @click="postponeGuide"
                >
                  {{ openType === 'manual' ? $t('onboarding.close') : $t('onboarding.checkLater') }}
                </ButtonSecondary>

                <div :style="{ flexGrow: 1 }"></div>
                <ButtonPrimary @click="next">{{ $t('onboarding.startGuide') }}</ButtonPrimary>
              </template>
              <template v-else>
                <ButtonSecondary
                  width="auto"
                  @click="postponeGuide"
                >
                  {{ openType === 'manual' ? $t('onboarding.close') : $t('onboarding.checkLater') }}
                </ButtonSecondary>
                <div :style="{ flexGrow: 1 }"></div>
                <ButtonSecondary
                  width="50px"
                  min-width="none"
                  :style="{ minWidth: 'none' }"
                  @click="previous"
                >
                  <svg
                    width="8"
                    height="14"
                    viewBox="0 0 8 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.41619 12.7774L1.59219 6.94411L7.41619 1.12945C7.47827 1.06862 7.5276 0.996008 7.56127 0.915876C7.59494 0.835744 7.61228 0.749699 7.61228 0.66278C7.61228 0.575861 7.59494 0.489816 7.56127 0.409684C7.5276 0.329552 7.47827 0.256944 7.41619 0.196113C7.35636 0.132723 7.28394 0.0825407 7.20357 0.0487872C7.12321 0.0150337 7.03667 -0.00154562 6.94952 0.000113265C6.86253 -0.000429576 6.77633 0.0166578 6.69613 0.0503443C6.61592 0.0840307 6.54337 0.133618 6.48286 0.196113L0.192189 6.47745C0.0690789 6.60156 0 6.7693 0 6.94411C0 7.11893 0.0690789 7.28666 0.192189 7.41078L6.48286 13.7014C6.60813 13.8223 6.77543 13.8899 6.94952 13.8899C7.12362 13.8899 7.29092 13.8223 7.41619 13.7014C7.478 13.6414 7.52714 13.5696 7.56069 13.4902C7.59424 13.4109 7.61153 13.3256 7.61153 13.2394C7.61153 13.1533 7.59424 13.068 7.56069 12.9886C7.52714 12.9093 7.478 12.8375 7.41619 12.7774Z"
                      fill="currentColor"
                    />
                  </svg>
                </ButtonSecondary>
                <template v-if="isLastStep">
                  <ButtonPrimary
                    :style="{ width: 'auto' }"
                    @click="finishGuide"
                    >{{ $t('onboarding.finishGuide') }}</ButtonPrimary
                  >
                </template>
                <template v-else>
                  <ButtonPrimary
                    :style="{ width: 'auto' }"
                    @click="next"
                    >{{ $t('onboarding.next') }}</ButtonPrimary
                  >
                </template>
              </template>
            </div>
            <div
              v-if="stepIndex !== null"
              :class="$style.onboarding__progress"
            >
              <OnboardingProgress
                :step="stepIndex !== null ? stepIndex + 1 : null"
                :step-count="stepCount"
              />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { Paragraph1, Heading1 } from '~/components-daisy/typography';
import { ButtonPrimary } from '~/components-daisy/actions';
import useOnboarding from '~/composables/onboarding/useOnboarding';
import type { StepImage } from '~/onboarding/types';

const {
  currentStep,
  isWelcomeStep,
  stepCount,
  stepIndex,
  isLastStep,
  openType,
  postponeGuide,
  next,
  previous,
  finishGuide,
  readTime,
} = useOnboarding();
const { t } = useI18n();
const config = useRuntimeConfig();
const countryCode = config.public.unitCountryCode.toLowerCase() as CountryCode;

const stepDescription = computed(() => (currentStep.value ? t(currentStep.value.description).split('\n') : []));
const image = computed<StepImage | null>(() => {
  if (!currentStep.value) {
    return null;
  }

  return currentStep.value.image?.[countryCode] ?? currentStep.value.defaultImage ?? null;
});
const showGuide = ref(false);
const showOverlay = ref(false);

watch(
  currentStep,
  async (to, from) => {
    if (!from && to) {
      showOverlay.value = true;
      await nextTick();
      showGuide.value = true;
    } else if (from && !to) {
      showGuide.value = false;
    }
  },
  {
    immediate: true,
  }
);
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-out;
}

.slide-enter-from {
  transform: translate(0, 100%);
  @media (min-width: 900px) {
    transform: translate(100%, 0);
  }

  opacity: 0;
}

.slide-enter-to {
  transform: translate(0, 0);
  opacity: 1;
}

.slide-leave-from {
  transform: translate(0, 0);
  opacity: 1;
}

.slide-leave-to {
  transform: translate(0, 100%);
  @media (min-width: 900px) {
    transform: translate(100%, 0);
  }
  opacity: 0;
}
</style>

<style lang="postcss" module scoped>
.overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1000000;
}

.onboarding {
  position: fixed;
  display: flex;
  gap: var(--space-50);
  flex-direction: column;
  bottom: 0;
  right: 0;
  width: 100%;
  background: var(--color-mono-100);
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  height: 90vh;
  z-index: 1000000;

  @media (min-width: 900px) {
    top: 0;
    max-height: none;
    max-width: 640px;
    height: 100%;
    border-top-right-radius: 0;
    border-top-left-radius: 0;
  }

  &__imageContainer {
    margin: var(--space-200);
    max-height: 527px;

    &--steps {
      .onboarding__image > img {
        width: auto;
        height: 100%;
        border: 1px solid var(--color-mono-600, #4f4f4f);
        border-radius: var(--border-radius-100);
      }
    }

    &--welcome {
      .onboarding__image > img {
        width: 100%;
        height: 100%;
      }
    }
  }

  &__image {
    display: flex;
    justify-content: center;
    height: 100%;
    max-height: 45dvh;

    & > img {
      object-fit: contain;
      border-radius: var(--border-radius-100);
    }
  }

  &__content {
    padding: var(--space-200);
    display: flex;
    flex-direction: column;
    gap: var(--space-150);
    flex: 1 1 auto;
    overflow-y: auto;
  }

  &__title {
    color: var(--color-primary-800);
    text-align: center;
  }

  &__description {
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
    font: var(--font-500);
  }

  &__actions {
    display: flex;
    gap: var(--space-200);
    padding: var(--space-200);
  }

  &__progress {
    padding-block: var(--space-200) var(--space-100);
    padding-inline: var(--space-200);
  }
}

.readTime {
  color: var(--color-primary-800);
}
</style>

<style lang="css">
.richtext strong {
  font-weight: 700;
}
</style>
